import { buildAssignmentToPathLookup } from '@/utils/postingTemplate/structure';
import { isMappingValid } from '@/utils/postingTemplate/mappingStatus';
import { getValueIndex } from '@/utils/postingTemplate/repeatable';
import type { AttsAssignment, AttributeStructure } from '@/endpoints/channel/getAttributeSpec';
import type { CommonalityEnabled, CommonalityItem, CommonalityValueType } from '@/types/commonality';
import type {
  CommonalityApplication,
  CommonalityAssignmentRule,
  CommonalityByAssignment,
  CommonalityFilterType,
  MappingsByAssignment,
  PostingTemplateMapping,
} from '@/types/postingTemplate';

const FALLBACK_COMMONALITY_ID = 0;
export const COMMONALITY_ATTRIBUTE_VALUE = 'automatically applied';

export type CommonalityConfigInput = Partial<Omit<CommonalityItem, 'default_value_type' | 'default_value'>> &
  Pick<CommonalityItem, 'default_value_type'> & {
    default_value?: string | null;
  };

type AssignmentWithCommonalityKey = {
  atts_attribute?: {
    commonality_key?: string | null;
  };
};

export const isCommonalityValueType = (valueType: string | null | undefined): valueType is CommonalityValueType =>
  valueType === 'product_property' || valueType === 'string' || valueType === 'twig';

export const getAssignmentCommonalityKey = (
  assignment: AssignmentWithCommonalityKey | undefined,
  assignmentRule: CommonalityAssignmentRule | undefined
): string | undefined => assignment?.atts_attribute?.commonality_key ?? assignmentRule?.commonality_key ?? undefined;

const normalizeCommonalityItem = (commonalityKey: string, existing: CommonalityConfigInput): CommonalityItem => ({
  id: existing.id ?? FALLBACK_COMMONALITY_ID,
  created_at: existing.created_at ?? null,
  updated_at: existing.updated_at ?? null,
  commonality_key: existing.commonality_key ?? commonalityKey,
  default_value_type: existing.default_value_type,
  default_value: existing.default_value ?? '',
  subscription_id: existing.subscription_id ?? null,
  global_set_id: existing.global_set_id ?? null,
  grouping_id: existing.grouping_id ?? null,
  enabled: (existing.enabled ?? 1) as CommonalityEnabled,
  secondary_id: existing.secondary_id ?? null,
  secondary_id_type: existing.secondary_id_type ?? null,
});

export const getAssignmentCommonalityConfig = (
  assignment: AssignmentWithCommonalityKey | undefined,
  assignmentId: string | number,
  allCommonality: Record<string, CommonalityConfigInput>,
  commonalityByAssignment: CommonalityByAssignment = {}
): CommonalityItem | undefined => {
  const assignmentRule = commonalityByAssignment[String(assignmentId)];
  const commonalityKey = getAssignmentCommonalityKey(assignment, assignmentRule);
  if (!commonalityKey) return undefined;

  const existing = allCommonality[commonalityKey];
  if (assignmentRule && isCommonalityValueType(assignmentRule.value_type)) {
    const normalizedExisting = existing ? normalizeCommonalityItem(commonalityKey, existing) : undefined;
    return {
      id: normalizedExisting?.id ?? FALLBACK_COMMONALITY_ID,
      created_at: normalizedExisting?.created_at ?? null,
      updated_at: normalizedExisting?.updated_at ?? null,
      commonality_key: commonalityKey,
      default_value_type: assignmentRule.value_type,
      default_value: assignmentRule.attribute_value ?? '',
      subscription_id: normalizedExisting?.subscription_id ?? null,
      global_set_id: normalizedExisting?.global_set_id ?? null,
      grouping_id: normalizedExisting?.grouping_id ?? null,
      enabled: normalizedExisting?.enabled ?? 1,
      secondary_id: normalizedExisting?.secondary_id ?? null,
      secondary_id_type: normalizedExisting?.secondary_id_type ?? null,
    };
  }

  return existing ? normalizeCommonalityItem(commonalityKey, existing) : undefined;
};

export const isMappingEquivalentToCommonality = (
  mapping: PostingTemplateMapping,
  commonalityConfig: Pick<CommonalityItem, 'default_value_type' | 'default_value'>
): boolean =>
  mapping.value_type !== 'commonality' &&
  mapping.value_type === commonalityConfig.default_value_type &&
  (mapping.attribute_value ?? '') === (commonalityConfig.default_value ?? '');

export function findCommonalityApplications(args: {
  assignmentById: Record<string, AttsAssignment>;
  attributeStructure: Partial<AttributeStructure>;
  mappings: MappingsByAssignment;
  allCommonality: Record<string, CommonalityItem>;
  commonalityByAssignment?: CommonalityByAssignment;
  filterType?: CommonalityFilterType;
}): CommonalityApplication[] {
  const { assignmentById, attributeStructure, mappings, allCommonality, commonalityByAssignment = {}, filterType = 'unmapped' } = args;
  const attributesWithCommonality: CommonalityApplication[] = [];
  const assignmentToPathLookup = buildAssignmentToPathLookup(attributeStructure);

  Object.entries(assignmentById).forEach(([assignmentId, assignment]) => {
    if (assignment.is_mappable !== 1) return;

    const assignmentMappings = mappings[assignmentId] || [];
    // Applying commonality replaces every mapping with one row; never collapse a
    // repeatable-container leaf that is mapped in more than one row.
    if (assignmentMappings.some((mapping) => getValueIndex(mapping) > 0)) return;
    const commonalityConfig = getAssignmentCommonalityConfig(assignment, assignmentId, allCommonality, commonalityByAssignment);
    const commonalityKey = getAssignmentCommonalityKey(assignment, commonalityByAssignment[assignmentId]);

    if (!commonalityKey || !commonalityConfig) return;

    const validMappings = assignmentMappings.filter(isMappingValid);
    const usesCommonality = validMappings.some((mapping) => mapping.value_type === 'commonality');
    const manualMappings = validMappings.filter((mapping) => mapping.value_type !== 'commonality');
    const isRequired = assignment.min_occurs > 0;
    const isUnmapped = validMappings.length === 0;
    const hasRedundantMapping = !usesCommonality && manualMappings.some((mapping) => isMappingEquivalentToCommonality(mapping, commonalityConfig));
    const hasManualOverride = !usesCommonality && manualMappings.some((mapping) => !isMappingEquivalentToCommonality(mapping, commonalityConfig));

    const attributeData: CommonalityApplication = {
      assignment_id: parseInt(assignmentId, 10),
      attribute_path: assignmentToPathLookup[assignmentId] || `Assignment ${assignmentId}`,
      commonality_key: commonalityKey,
      commonality_config: commonalityConfig,
      is_required: isRequired,
      is_unmapped: isUnmapped,
      has_redundant_mapping: hasRedundantMapping,
      has_manual_override: hasManualOverride,
    };

    switch (filterType) {
      case 'required':
        if (isRequired && isUnmapped && assignment.ignore_validation !== 1) {
          attributesWithCommonality.push(attributeData);
        }
        break;
      case 'redundant':
        if (hasRedundantMapping) {
          attributesWithCommonality.push(attributeData);
        }
        break;
      case 'overridden':
        if (hasManualOverride) {
          attributesWithCommonality.push(attributeData);
        }
        break;
      case 'unmapped':
      default:
        if (isUnmapped) {
          attributesWithCommonality.push(attributeData);
        }
        break;
    }
  });

  return attributesWithCommonality;
}
